/**
 * TODO:
 * hard loading room for rent listing removes steps from the config and the steps sequencing is not accurate
 * we need to handle canAccessModularListingRoomForRent server side or display a spinner on initial load before we render the routes component
 *
 * adding a setTimeout after the get details api call to allow for more time for the redux-store to be updated
 * this is not an efficient solution and should be removed once we have a better solution
 */

/**
 * The dynamically rendering steps is becoming more difficult to maintain.
 * Order for removal is becoming a factor.
 *
 * We need to dynamically regenerate the entire config file via conditional params instead of one off addStep, removeStep
 * This will also need to include some design work to account for loading state (we do not have loading within the body content of the steps)
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Toast, useToast } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';
import { useRentalManagerUserPropertyMultiEditWebApiHandler_multi } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { cloneDeep } from 'es-toolkit';
import { parseQueryString } from '@zg-rentals/string-utils';
import {
  FeeType,
  useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType,
  useRentalManagerUserPropertyToursWebApiHandler_tours,
} from '@zg-rentals/rental-manager-env';
import { useQueryClient } from '@zg-rentals/react-query5';
import { logError } from '@zg-rentals/log-error';
import { TOUR_CALENDAR_MODES, useTourCalendarApi } from '@zg-rentals/touring';

import ExternalTour from '../../model/ExternalTour';
import VrModel from '../../model/VrModel';
import WizardServerError from '../../model/WizardServerError';

import accessUtils from '../../utils/accessUtils';
import propertyUtils from '../../utils/propertyUtils';

import { addStep, removeStep } from '../../utils/wizardUtils';

import { FreemiumContext } from '../../components/Freemium/context';
import { PaymentGatewayContext } from '../../components/PaymentGateway/context';
import { RMWContext } from '../../providers/RMWContext';
import { TourSchedulingProvider } from '../../components/WizardModularListing/TourSchedulingSetup/context';

import { INIT_STATE as PAYMENT_GATEWAY_INIT_STATE } from '../../components/PaymentGateway/reducer';

import { RoutingSelectors } from '../../selectors';

import { updateIsWizardView } from '../../slices/app';
import { default as freemiumActions } from '../../components/Freemium/actions';
import { default as pgActions } from '../../components/PaymentGateway/actions';
import PostingActions from '../../actions/PostingActions';
import PropertiesActions from '../../actions/PropertiesActions';
import actions from './actions';

import { DISPLAY_SERVER_ERROR_AT, EXIT_CTA_LABEL } from '../../constants/WizardConstants';
import { CREATE_LISTING_PAGE, LISTING } from '../../constants/RouteConstants';
import { STEP_DIRECTION } from '../../components/WizardStepController/constants';
import { serverErrorFieldName } from '../../components/WizardModularListing/constants';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../constants/FreemiumConstants';

import AuthCheckWrapper from '../AuthCheckWrapper';
import ErrorPage from '../ErrorPage';

import PermissionCheck from '../../components/PermissionCheck';
import PremiumPurchaseModal from '../../components/Modal/PremiumPurchaseModal';
import SpinnerContainer from '../../components/SpinnerContainer';
import WizardStepController from '../../components/WizardStepController';
import HeadWrapper from '../../components/HeadWrapper';

// hooks
import { useGetisTopOfFunnelEnrollmentEligible } from '../../hooks/queries/freemium';

import {
  getTourSchedulingRedirectStep,
  handleApplicationsStepLogicChange,
  handleFastPostSteps,
  handleForRentByChange,
  handlePostingPathTourSchedulingSteps,
  handleRelistingSteps,
  handleRoomForRentFlowChange,
  handleRoomForRentSteps,
  handleZRMCostsAndFeesSteps,
  isTourSchedulingRedirectStep,
  updateRoomForRentFlowContextByForRentBy,
} from './utils';
import { ModularListingContext } from './context';
import { ACTION } from './reducer';

import {
  PROGRESS_STEP,
  PROGRESS_STEP_CONFIG,
  PROGRESS_STEP_LABEL,
  PROGRESS_STEP_RELISTING_CONFIG,
  PROGRESS_STEP_RELISTING_TOUR_BOOKING_CONFIG,
  RELISTING_STEP_SUFFIX,
  STEP,
  STEP_DELIMITER,
} from './constants';

import TimeConstants from '../../constants/TimeConstants';
import AddPropertyConstants from '../../constants/AddPropertyConstants';

import {
  STEP_TO_STEP_GROUP_MAP as aStepToStepGroupMap,
  config,
  configStepGroupRelist,
  configStepGroupReview,
  configStepGroupTourBooking,
  ORIGINAL_CONFIG,
  STEP_TO_STEP_RELISTING_GROUP_MAP,
  STEP_TO_STEP_RELISTING_TOUR_BOOKING_GROUP_MAP,
} from './config';

import { useGetListingDetails } from '../../hooks/queries/properties';

import ReviewPageCSEvents from '../../model/ClickstreamEvent/ReviewPageCSEvents';
import CurrentProperty from '../../model/CurrentProperty';
import ModularListingCSEvents from '../../model/ClickstreamEvent/ModularListingCSEvents';
import ErrorRMW from '../../model/ErrorRMW';
import { ERROR_PAGE_TYPES } from '../../constants/ErrorMessageConstants';

import AddPropertyModal from '../../components/Modal/AddPropertyModal';
import TransferFeedListingModal from '../../components/Modal/TransferFeedListingModal';

import { costsAndFeesConstraints } from '../../validation';
import useAccessPostingPathTourScheduling from '../../hooks/useAccessPostingPathTourScheduling';

const DEFAULT_STEP = STEP.ROOM_WHO_IS_LISTING_PROPERTY;
const DEFAULT_STEP_FOR_REACTIVATION = STEP.REVIEW;
const DEFAULT_STEP_FOR_RELISTING = STEP.UPDATE_RECENTLY_CHANGED;

const removedStepMap = new Map();

const propTypes = {};
const defaultProps = {};

// TODO: optimize this into a hook
// config stable detection
// no config changes after timeout
const DETECT_STABLE_CONFIG_TIMEOUT = 1000;
const COUNTDOWN_INTERVAL = 1000;

const CreateListingPage = () => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const { enqueueToast } = useToast();

  const { paths } = useContext(RMWContext);
  const { freemiumReducerDispatch } = useContext(FreemiumContext);

  const { modularListingState, reducerDispatch } = useContext(ModularListingContext);
  const {
    pgState: { isResponseSuccess },
    reducerDispatch: pgReducerDispatch,
  } = useContext(PaymentGatewayContext);

  const {
    app: { cookieEnabledServices, isWizardView },
    properties: {
      currentProperty,
      currentProperty: {
        active,
        canBeRelisted,
        dateLastActivatedTimestamp,
        displayTotalMonthlyPriceWithFee,
        floorplans,
        forRentBy,
        freemium: {
          activeProduct: { type: activeProductType },
          isEnabled: isFreemiumEnabled,
          products: freemiumProducts,
        } = {},
        isFeed,
        isMultifamily,
        listingAlias,
        listingCriteria: { isEnabled: isListingCriteriaEnabled },
        modularListingProgress,
        restrictions: {
          applications: { isUpdateRestricted: isApplicationsUpdateRestricted } = {},
          isHidePhoneRestricted,
        } = {},
        totalMonthlyPriceWithFeeNotice: { isEligible: isTotalMonthlyPriceWithFeeEligible } = {},
      },
    },
    relist: { renderAdditionalDetailsPage = true },
    user,
  } = useSelector((state) => state);
  const search = useSelector((state) => RoutingSelectors.makeSearch(state));
  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));

  const [displayAddPropertyModal, setDisplayAddPropertyModal] = useState(false);
  const [configObj, setConfigObj] = useState({
    STEP_TO_STEP_GROUP_MAP: { ...aStepToStepGroupMap },
    CONFIG: [...config],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isConfigStable, setIsConfigStable] = useState(false);
  const [currentStepKey, setCurrentStepKey] = useState(DEFAULT_STEP);
  const [defaultStepKey, setDefaultStepKey] = useState(DEFAULT_STEP);
  const [fetchListingError, setFetchListingError] = useState(null);
  const [progressSteps, setProgressSteps] = useState(PROGRESS_STEP_CONFIG);
  const [hasListingDetailsLoaded, setHasListingDetailsLoaded] = useState(false);
  const [selectedBookingMethodForTourScheduling, setSelectedBookingMethodForTourScheduling] = useState(null);

  const initialRender = useRef(true);
  const isGetDetailsFetching = useRef(null);
  const intervalStableConfigRef = useRef(null);

  const {
    data: getListingDetailsData,
    error: getListingError,
    isError: isGetListingDetailsError,
    isLoading: isGetListingDetailsLoading,
  } = useGetListingDetails(listingId);

  const isActiveProductTypePremium = activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;
  const { data: isTopOfFunnelEnrollmentEligible } = useGetisTopOfFunnelEnrollmentEligible({
    enabled: !isActiveProductTypePremium,
  });

  const { data: toursData, isLoading: isToursLoading } = useRentalManagerUserPropertyToursWebApiHandler_tours(
    { propertyId: listingAlias },
    {
      enabled: Boolean(!isLoading) && listingAlias !== '',
    },
  );

  const { mutateAsync: saveMultipleFieldsPropertyInfoMutate } =
    useRentalManagerUserPropertyMultiEditWebApiHandler_multi();
  const saveMultipleFieldsPropertyInfo = (data) => saveMultipleFieldsPropertyInfoMutate({ postData: data });

  const canAccessSFCostsAndFees = useMemo(() => {
    if (hasListingDetailsLoaded) {
      return accessUtils.canAccessSFCostsAndFees({
        isFeed,
        isMultifamily,
      });
    }

    return false;
  }, [hasListingDetailsLoaded, isMultifamily, isFeed]);

  const { data: costsAndFeesData } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_get(
    {
      listingAlias,
    },
    {
      enabled: canAccessSFCostsAndFees && listingAlias !== '',
    },
  );

  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType(
    {
      feeType: FeeType.APPLICATION_FEE,
      listingAlias,
    },
    {
      enabled: canAccessSFCostsAndFees && listingAlias !== '',
    },
  );

  const { mutateAsync: setDisplayTotalMonthlyPriceWithFee } =
    useRentalManagerUserPropertyEditWebApiHandler_displayTotalMonthlyPriceWithFee();

  const canAccessRelistingFlow = accessUtils.canAccessRelistingFlow({
    canBeRelisted,
  });

  const canAccessFastPost = accessUtils.canAccessFastPost({
    currentProperty,
  });

  const { canAccessPostingPathTourScheduling } = useAccessPostingPathTourScheduling();

  const { initialZoneIdWithoutTourConfig } = useTourCalendarApi({
    enableQueries: Boolean(canAccessPostingPathTourScheduling && listingAlias),
    listingAlias,
    mode: TOUR_CALENDAR_MODES.TOUR_AVAILABILITY,
  });

  const canAccessTopOfFunnelPremium = useMemo(
    () =>
      accessUtils.canAccessTopOfFunnelPremium({
        isFreemiumEnabled,
        isTopOfFunnelEnrollmentEligible,
        isActiveProductTypePremium,
      }),
    [isTopOfFunnelEnrollmentEligible, isFreemiumEnabled, isActiveProductTypePremium],
  );

  const canAccessScreeningCriteriaInListingFlow = isListingCriteriaEnabled;

  // deep linking and listingCloneConversionSuccess Toast
  const { step: deepLinkStep, listingCloneConversionSuccess } = parseQueryString(search.toString());

  const {
    hasConfirmedSaveAndExit,
    hasFieldError,
    isInReactivationFlow,
    isWizardComplete,
    roomForRentFlow,
    serverErrorObj,
    isInModalState,
    isPremiumPurchaseModalOpen,
  } = modularListingState;
  const { CONFIG, STEP_TO_STEP_GROUP_MAP = {} } = configObj || {};

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });
  const { formattedStreet } = propertyUtils.formatAddress(currentProperty);
  const titleContent = `Edit ${formattedStreet || 'listing'}`;

  const isNextDisabled = isInModalState === false && hasFieldError;
  const isBackDisabled = isInModalState === false && hasFieldError;

  const accessUtilsCanAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    cookieEnabledServices,
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  useEffect(() => {
    if (isToursLoading === false && toursData) {
      syncCurrentProperty3dTours({
        vrModels: toursData.vrModels,
        externalTours: toursData.externalTours,
      });
    }
  }, [toursData, isToursLoading]);

  useEffect(() => {
    if (costsAndFeesData?.dictionaryItems) {
      costsAndFeesConstraints.getcostsAndFeesConstraints(costsAndFeesData?.dictionaryItems);
    }
  }, [costsAndFeesData?.dictionaryItems]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    let countdown = DETECT_STABLE_CONFIG_TIMEOUT / COUNTDOWN_INTERVAL;

    const checkConfigStability = () => {
      if (countdown === 0) {
        setIsConfigStable(true);
        clearInterval(intervalStableConfigRef.current);

        return;
      }

      countdown = countdown - 1;
    };

    // clear any existing interval before setting a new one
    if (intervalStableConfigRef.current) {
      clearInterval(intervalStableConfigRef.current);
    }

    intervalStableConfigRef.current = setInterval(checkConfigStability, COUNTDOWN_INTERVAL);

    // cleanup
    return () => {
      if (intervalStableConfigRef.current) {
        clearInterval(intervalStableConfigRef.current);
      }
    };
  }, [CONFIG.length, isLoading]);

  useEffect(() => {
    if (initialRender.current) {
      if (!isWizardView) {
        dispatch(updateIsWizardView(true));
      }

      initialRender.current = false;
    }

    // resetting `pgState` to `PAYMENT_GATEWAY_INIT_STATE` on landing
    pgActions.updateAllData({
      reducerDispatch: pgReducerDispatch,
      data: {
        ...PAYMENT_GATEWAY_INIT_STATE,
        responseData: {},
      },
    });

    return () => {
      updateModularListingContext({ isInReactivationFlow: false });

      actions.updateHasConfirmedSaveAndExit({ reducerDispatch, hasConfirmedSaveAndExit: false });
      actions.updateDisplaySaveAndExitModal({ reducerDispatch, displaySaveAndExitModal: false });

      dispatch(updateIsWizardView(false));
    };
  }, []);

  useEffect(() => {
    if (isGetListingDetailsLoading === false) {
      handleListingDetailsLoaded({ getListingDetailsData });
    }
  }, [isGetListingDetailsLoading]);

  useEffect(() => {
    if (isGetListingDetailsError) {
      const errorRMWModel = ErrorRMW(getListingError.error);

      logError({
        message: errorRMWModel,
        context: 'PostingActions.getDetails.CreateListingPage',
      });
    }
  }, [isGetListingDetailsError, getListingError]);

  useEffect(() => {
    const scopeDefaultStepKey = getDefaultStep();

    setDefaultStepKey(scopeDefaultStepKey);
    if (isGetDetailsFetching.current === false) {
      checkMissingStepAndUpdate(scopeDefaultStepKey);
    }
  }, [canAccessRelistingFlow, dateLastActivatedTimestamp, isFeed, isMultifamily]);

  useEffect(() => {
    if (hasConfirmedSaveAndExit) {
      handleSaveAndExit();
    }
  }, [hasConfirmedSaveAndExit]);

  useEffect(() => {
    if (!isLoading && listingAlias !== '') {
      handleRoomForRentSteps({ isRoomForRent, removeFromWizardConfig });
      /**
       * CAUTION: double check logic when adding/removing steps as there are side effects
       * from other handlers in the util file.
       */
      handleFastPostSteps({
        canAccessFastPost,
        currentProperty,
        dispatch,
        saveMultipleFieldsPropertyInfo,
        enqueueToast,
        addToWizardConfig,
        removeFromWizardConfig,
      });
    }
  }, [canAccessFastPost, isLoading, isInReactivationFlow, listingAlias]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (listingAlias !== '') {
      handlePostingPathTourSchedulingSteps({
        canAccessPostingPathTourScheduling,
        addToWizardConfig,
        removeFromWizardConfig,
        canAccessRelistingFlow,
        selectedBookingMethod: selectedBookingMethodForTourScheduling,
        initialZoneIdWithoutTourConfig,
      });
    }
  }, [
    isLoading,
    listingAlias,
    canAccessPostingPathTourScheduling,
    canAccessRelistingFlow,
    selectedBookingMethodForTourScheduling,
    initialZoneIdWithoutTourConfig,
  ]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    handleApplicationsStepLogicChange({
      addToWizardConfig,
      canAccessFastPost,
      isApplicationsUpdateRestricted,
      removeFromWizardConfig,
    });
  }, [canAccessFastPost, isApplicationsUpdateRestricted, isLoading]);

  useEffect(() => {
    if (
      ((!canAccessRelistingFlow && !isLoading) || (canAccessRelistingFlow && !isLoading && !isToursLoading)) &&
      listingAlias !== ''
    ) {
      handleRelistingSteps({
        dispatch,
        saveMultipleFieldsPropertyInfo,
        canAccessRelistingFlow,
        addToWizardConfig,
        removeFromWizardConfig,
        currentProperty,
        isRoomForRent,
        enqueueToast,
        toursData,
        canAccessSFCostsAndFees,
      });
    }
  }, [canAccessRelistingFlow, isLoading, isToursLoading, listingAlias]);

  useEffect(() => {
    if (isRoomForRent) {
      updateRoomForRentFlowContextByForRentBy({ forRentBy, actions, reducerDispatch });
    }

    if (!forRentBy || canAccessFastPost) {
      return;
    }

    handleForRentByChange({
      forRentBy,
      isHidePhoneRestricted,
      removeFromWizardConfig,
      addToWizardConfig,
      actions,
      reducerDispatch,
    });
  }, [isRoomForRent, forRentBy]);

  useEffect(() => {
    handleRoomForRentFlowChange({
      isRoomForRent,
      roomForRentFlow,
      removeFromWizardConfig,
      addToWizardConfig,
    });
  }, [roomForRentFlow]);

  useEffect(() => {
    if (isResponseSuccess) {
      clearGetListingSummaryQueryCache();
      queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });
    }
  }, [isResponseSuccess]);

  useEffect(() => {
    freemiumActions.updateCanAccessTopOfFunnelPremium({
      reducerDispatch: freemiumReducerDispatch,
      canAccessTopOfFunnelPremium,
    });
  }, [canAccessTopOfFunnelPremium]);

  useEffect(() => {
    if (
      canAccessRelistingFlow &&
      currentStepKey === STEP.CONSIDER_ADDITIONAL_DETAILS &&
      renderAdditionalDetailsPage === false &&
      !isToursLoading &&
      !isLoading
    ) {
      setCurrentStepKey(STEP.REVIEW);
    }
  }, [renderAdditionalDetailsPage, canAccessRelistingFlow, currentStepKey, isToursLoading, isLoading]);

  /**
   * reactivation flow check
   */
  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isInReactivationFlow) {
      let clonedProgressSteps = [...progressSteps];

      if (canAccessPostingPathTourScheduling && canAccessRelistingFlow) {
        clonedProgressSteps = PROGRESS_STEP_RELISTING_TOUR_BOOKING_CONFIG;
      } else if (canAccessRelistingFlow) {
        clonedProgressSteps = PROGRESS_STEP_RELISTING_CONFIG;
      }

      setProgressSteps(clonedProgressSteps);
    }
  }, [isLoading, isInReactivationFlow, canAccessPostingPathTourScheduling, canAccessRelistingFlow]);

  useEffect(() => {
    /**
     * solved a race condition where both functions are updating progressSteps
     * setProgressSteps gets called in the last function
     */
    triggerCostAndFeesCheck().then(triggerScreeningCriteriaInListingFlowCheck);
  }, [
    canAccessScreeningCriteriaInListingFlow,
    canAccessSFCostsAndFees,
    isFeed,
    isGetDetailsFetching.current,
    isInReactivationFlow,
    isLoading,
  ]);

  // wizard title
  let wizardTitle = '';
  if (!isFreemiumEnabled && currentStepKey === STEP.PAY_AND_PUBLISH) {
    wizardTitle = PROGRESS_STEP_LABEL.PUBLISH;
  } else if (
    canAccessRelistingFlow &&
    (currentStepKey === STEP.UPDATE_RECENTLY_CHANGED || currentStepKey === STEP.CONSIDER_ADDITIONAL_DETAILS)
  ) {
    wizardTitle = STEP_TO_STEP_RELISTING_GROUP_MAP[currentStepKey]?.label || '';
  } else if (
    canAccessRelistingFlow &&
    canAccessPostingPathTourScheduling &&
    (currentStepKey === STEP.TOUR_SCHEDULING_BOOKING_METHOD_RELIST ||
      currentStepKey === STEP.TOUR_SCHEDULING_TIMEZONE_RELIST ||
      currentStepKey === STEP.TOUR_SCHEDULING_AVAILABILITY_RELIST)
  ) {
    wizardTitle = STEP_TO_STEP_RELISTING_TOUR_BOOKING_GROUP_MAP[currentStepKey]?.label || '';
  } else if (STEP_TO_STEP_GROUP_MAP[currentStepKey]) {
    wizardTitle = STEP_TO_STEP_GROUP_MAP[currentStepKey].label;
  } else if (!STEP_TO_STEP_GROUP_MAP[defaultStepKey]?.label) {
    setCurrentStepKey(CONFIG[0].stepKey);
  } else {
    setCurrentStepKey(defaultStepKey);
    wizardTitle = STEP_TO_STEP_GROUP_MAP[defaultStepKey]?.label;
  }

  // TODO: DRY this function
  const syncCurrentProperty3dTours = ({ vrModels, externalTours, units = [] }) => {
    const copyFloorplans = cloneDeep(floorplans);
    const copyUnits = cloneDeep(units);
    const vrModelsMap = {};
    const externalToursMap = {};

    const formattedVrModels = vrModels.map(VrModel);
    const formattedExternalTours = externalTours.map(ExternalTour);

    // iterate through the api response and make vrModelsMap/externalToursMap so the model can be searched by reference id.
    // EXAMPLE OF THE KEY BEING THE REFERENCE ID (FOR externalToursMap/VrToursMap)
    //  {
    //     "InvitationHomes/05a252e7-bb89-48fb-b909-157c9d28aea5": {
    //         "createdTimestamp": 1656701864601,
    //         "isReadOnly": true,
    //         "label": "",
    //         "referenceId": "InvitationHomes/05a252e7-bb89-48fb-b909-157c9d28aea5",
    //         "sourceName": "Inside maps",
    //         "status": "published",
    //         "tourUrl": "https://www.insidemaps.com/app/walkthrough-v2?projectId=8xLqhGvAGH&env=production&embedded=true"
    //     }
    // }
    formattedVrModels.forEach((tour) => {
      vrModelsMap[tour.referenceId] = tour;
    });
    formattedExternalTours.forEach((tour) => {
      externalToursMap[tour.referenceId] = tour;
    });

    copyFloorplans.forEach((floorplan, floorplanIndex) => {
      if (vrModelsMap[floorplan.floorPlanId]) {
        copyFloorplans[floorplanIndex].vrModel = vrModelsMap[floorplan.floorPlanId];

        const indexOfVrModel = formattedVrModels.findIndex((element) => element.referenceId === floorplan.floorPlanId);

        if (indexOfVrModel > -1) {
          // if the vrModel referenceId matches the floorplan.floorplanId, remove the vrModel from the
          // formattedVrModels array since it is now part of the floorplan. Any remaining vrModels left in vrModels
          // will be attached to the listing (not a floorplan or unit)
          formattedVrModels.splice(indexOfVrModel, 1);
        }
      }

      if (externalToursMap[floorplan.floorPlanId]) {
        // if the external referenceId matches the floorplan.floorplanId, remove the externalTour from the
        // formattedExternalTours array since it is now part of the floorplan. Any remaining externalTours left in
        // externalTours will be attached to the listing (not a floorplan or unit)
        copyFloorplans[floorplanIndex].externalTour = externalToursMap[floorplan.floorPlanId];
        const indexOfExternalTour = formattedExternalTours.findIndex(
          (element) => element.referenceId === floorplan.floorPlanId,
        );

        if (indexOfExternalTour > -1) {
          formattedExternalTours.splice(indexOfExternalTour, 1);
        }
      }

      if (floorplan.units.length > 0) {
        floorplan.units.forEach((unit, unitIndex) => {
          if (vrModelsMap[unit.unitId]) {
            // same concept here as above but now we are iterating the the units in a floorplan and attaching a matching vrModel reference id to the unit.
            copyFloorplans[floorplanIndex].units[unitIndex].vrModel = vrModelsMap[unit.unitId];

            const indexOfVrModel = formattedVrModels.findIndex((element) => element.referenceId === unit.unitId);

            if (indexOfVrModel > -1) {
              formattedVrModels.splice(indexOfVrModel, 1);
            }
          }
          if (externalToursMap[unit.unitId]) {
            copyFloorplans[floorplanIndex].units[unitIndex].externalTour = externalToursMap[unit.unitId];

            const indexOfExternalTour = formattedExternalTours.findIndex(
              (element) => element.referenceId === unit.unitId,
            );

            if (indexOfExternalTour > -1) {
              formattedExternalTours.splice(indexOfExternalTour, 1);
            }
          }
        });
      }
    });

    copyUnits.forEach((unit, unitIndex) => {
      if (vrModelsMap[unit.unitId]) {
        // same concept here as above but now we are iterating the the units without floorplan and attaching a
        // matching vrModel reference id to the unit.
        copyUnits[unitIndex].vrModel = vrModelsMap[unit.unitId];

        const indexOfVrModel = formattedVrModels.findIndex((element) => element.referenceId === unit.unitId);

        if (indexOfVrModel > -1) {
          formattedVrModels.splice(indexOfVrModel, 1);
        }
      }
      if (externalToursMap[unit.unitId]) {
        copyUnits[unitIndex].externalTour = externalToursMap[unit.unitId];

        const indexOfExternalTour = formattedExternalTours.findIndex((element) => element.referenceId === unit.unitId);

        if (indexOfExternalTour > -1) {
          formattedExternalTours.splice(indexOfExternalTour, 1);
        }
      }
    });

    // any leftover formattedVrModels/formattedExternalTours (that haven't been spliced out) need to be filtered out
    // because sometimes the BE returns  vrModels that are not associated at the listing, floorplan, or unit level.
    // The way to know if a listing  is specifically meant for listing level is if the reference id contains
    // "feedid/feedListingId" not "feedid/feedListingId/feedModelId". Simplest way is to check how long the
    // referenceId is after being split on '/'.
    dispatch(
      PropertiesActions.updateCurrentProperty({
        vrModels: formattedVrModels.filter((vrModel) => {
          const referenceId = vrModel.referenceId.split('/');
          return referenceId.length === 2;
        }),
        externalTours: formattedExternalTours.filter((externalTour) => {
          const referenceId = externalTour.referenceId.split('/');
          return referenceId.length === 2;
        }),
        floorplans: copyFloorplans,
        units: copyUnits,
      }),
    );

    return { vrModels, externalTours };
  };

  const triggerCostAndFeesCheck = async () => {
    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        canAccessSFCostsAndFees,
      },
    });

    if (!isLoading && !isInReactivationFlow && !isGetDetailsFetching.current) {
      const progressStepsToRemove = !canAccessSFCostsAndFees ? [PROGRESS_STEP.COSTS_AND_FEES] : [];

      handleZRMCostsAndFeesSteps({
        canAccessSFCostsAndFees,
        removeFromWizardConfig,
        addToWizardConfig,
      });

      return progressSteps.filter((step) => !progressStepsToRemove.includes(step.key));
    }

    return progressSteps;
  };

  /**
   * screening criteria in listing flow access check
   *
   * if there is no access we need to remove the entire progress step group
   */
  const triggerScreeningCriteriaInListingFlowCheck = async (filteredProgressSteps) => {
    if (!isInReactivationFlow && !isLoading && !isGetDetailsFetching.current) {
      const progressStepsToRemove = !canAccessScreeningCriteriaInListingFlow ? [PROGRESS_STEP.SCREENING_CRITERIA] : [];

      setProgressSteps(filteredProgressSteps.filter((step) => !progressStepsToRemove.includes(step.key)));

      if (!canAccessScreeningCriteriaInListingFlow) {
        removeFromWizardConfig(STEP.FINANCIAL_EXPECTATIONS);
        removeFromWizardConfig(STEP.PET_POLICY_AND_PET_FEE);
      } else {
        removeFromWizardConfig(STEP.PET_POLICY);
      }
    }

    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        canAccessScreeningCriteriaInListingFlow,
      },
    });
  };

  /**
   * getDetails endpoint has loaded
   */
  const handleListingDetailsLoaded = ({ getListingDetailsData: response }) => {
    setHasListingDetailsLoaded(true);

    let isInRelistingReview = false;
    let currentPropertyCastedResponse;
    let validStepUpperCase;

    currentPropertyCastedResponse = CurrentProperty(response);

    const scopeDefaultStepKey = getDefaultStep();

    let { modularListingProgress: { step = '' } = {}, isAddressValid } = currentPropertyCastedResponse;

    const canAccessMLReactivationFlow = accessUtils.canAccessMLReactivationFlow({
      currentProperty: currentPropertyCastedResponse,
    });

    if (step) {
      const stepSplit = step.split(STEP_DELIMITER);
      if (stepSplit[1] === RELISTING_STEP_SUFFIX) {
        isInRelistingReview = true;
        step = stepSplit[0];
      }
    }

    // TODO: check if this does anything.  ACTION.UPDATE_DATA is not an action for reducerDispatch
    reducerDispatch({
      type: ACTION.UPDATE_DATA,
      payload: { data: currentPropertyCastedResponse },
    });

    if (currentPropertyCastedResponse.active) {
      ReviewPageCSEvents.ActiveListingView({
        user,
        currentProperty: currentPropertyCastedResponse,
      });
    }

    if (propertyUtils.isPreviouslyActive({ currentProperty: currentPropertyCastedResponse })) {
      ReviewPageCSEvents.OffMarketListingView({
        user,
        currentProperty: currentPropertyCastedResponse,
      });
    }

    updateModularListingContext({ isInReactivationFlow: canAccessMLReactivationFlow });

    if (!isAddressValid && !isFeed) {
      setDisplayAddPropertyModal(true);
    }

    const canAccessRelistingFlow = accessUtils.canAccessRelistingFlow({
      canBeRelisted: currentPropertyCastedResponse.canBeRelisted,
    });
    // handle listing reactivation above deep link check
    const didRedirect = checkRedirectToReactivationFlow({
      canAccessMLReactivationFlow,
      targetStep: step,
      isInRelistingReview,
      canAccessRelistingFlow,
    });

    if (didRedirect) {
      mockListingDetailsSettled();

      return;
    }

    // User cannot resume tour scheduling setup so we need to reset the step to the beginning of the tour scheduling flow
    if (isTourSchedulingRedirectStep(step)) {
      step = getTourSchedulingRedirectStep(step);
    }

    // deep link takes priority
    if (deepLinkStep) {
      validStepUpperCase = STEP[deepLinkStep.toUpperCase()] || scopeDefaultStepKey;

      setCurrentStepKey(validStepUpperCase);
      saveStepProgress(validStepUpperCase);

      mockListingDetailsSettled();

      return;
    }

    // handle step if it exists on api response. missing step is handled in checkMissingStepAndUpdate via useEffect
    if (step) {
      validStepUpperCase = STEP[step.toUpperCase()] || scopeDefaultStepKey;

      if (
        canAccessRelistingFlow &&
        isValidRelistingStep({ currentStepKey: validStepUpperCase, canAccessPostingPathTourScheduling }) === false
      ) {
        validStepUpperCase = scopeDefaultStepKey;

        saveStepProgress(validStepUpperCase);
      }

      setCurrentStepKey(validStepUpperCase);

      if (step.toUpperCase() === validStepUpperCase) {
        trackContinueFrom(validStepUpperCase);
      }
    }

    mockListingDetailsSettled();

    if (listingCloneConversionSuccess === 'true') {
      enqueueToast(<Toast tone="success">Successfully created a new listing</Toast>);
    }
  };

  const handleListingDetailsError = (errorRMWModel) => {
    mockListingDetailsSettled();
    setFetchListingError(errorRMWModel);
  };

  const mockListingDetailsSettled = () => {
    isGetDetailsFetching.current = false;

    /**
     * TODO: render component once all required data is settled
     *
     * redux stats are still being set in shared/app/index
     *
     * ex: canAccessModularListingRoomForRent
     */
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const checkRedirectToReactivationFlow = ({
    canAccessMLReactivationFlow,
    targetStep,
    isInRelistingReview,
    canAccessRelistingFlow,
  }) => {
    let didRedirect = false;

    if (canAccessMLReactivationFlow) {
      const validStepUpperCase = deepLinkStep || STEP[targetStep?.toUpperCase()] || defaultStepKey;
      const isListingActiveAndValidStepPayAndPublish = validStepUpperCase === STEP.PAY_AND_PUBLISH && active;

      if (isFeed || isListingActiveAndValidStepPayAndPublish || canAccessMLReactivationFlow || canAccessRelistingFlow) {
        let targetStepKey = STEP.REVIEW;

        if (canAccessRelistingFlow) {
          if (targetStep === STEP.REVIEW.toLowerCase() && !isInRelistingReview) {
            targetStepKey = STEP.UPDATE_RECENTLY_CHANGED;
          } else {
            return didRedirect;
          }
        }

        setCurrentStepKey(targetStepKey);

        if (!isFeed) {
          saveStepProgress(targetStepKey);
        }

        didRedirect = true;
      }
    }

    return didRedirect;
  };

  const updateModularListingContext = ({ isInReactivationFlow }) => {
    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        isInModularListingContext: true,
        isInReactivationFlow,
      },
    });
  };

  /**
   * check if modular listing progress is missing the step key
   * if so, then update and save the step progress with the default step based on certain params
   */
  const checkMissingStepAndUpdate = useCallback(
    debounce((scopeDefaultStepKey) => {
      if (!modularListingProgress?.step) {
        saveStepProgress(scopeDefaultStepKey);
        setCurrentStepKey(scopeDefaultStepKey);
        trackContinueFrom(scopeDefaultStepKey);
      }
    }, TimeConstants.DEBOUNCE_TIME_500_MS),
    [],
  );

  const clearGetListingSummaryQueryCache = () => {
    // invalidate query to sync: active, modularListingProgress
    queryClient.invalidateQueries({ queryKey: ['getListingSummary', listingAlias] });
  };

  const clearServerError = () => {
    if (serverErrorObj.displayMessage) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const removeFromWizardConfig = (stepKey) => {
    if (!stepKey) {
      return;
    }

    const responseConfigObj = removeStep({
      CONFIG,
      STEP_TO_STEP_GROUP_MAP,
      stepKey,
      removedStepMap,
      ORIGINAL_CONFIG,
    });

    updateConfigObj(responseConfigObj);
  };

  const addToWizardConfig = (stepKey) => {
    if (!stepKey) {
      return;
    }

    const responseConfigObj = addStep({
      CONFIG,
      STEP_TO_STEP_GROUP_MAP,
      stepKey,
      removedStepMap,
      ORIGINAL_CONFIG,
    });

    updateConfigObj(responseConfigObj);
  };

  const goToDashboardPage = () => {
    window.router.transitionTo(`${paths.propertiesPage}/${listingId}/dashboard/overview`, {
      clpProgressSaveSuccess: true,
    });
  };

  const exit = async () => {
    try {
      if (accessUtilsCanAccessTotalMonthlyPriceWithFee && active && !displayTotalMonthlyPriceWithFee) {
        await setDisplayTotalMonthlyPriceWithFee({
          getParams: { listingAlias, displayTotalMonthlyPriceWithFee: true },
        });
      }
    } finally {
      clearGetListingSummaryQueryCache();
      dispatch(updateIsWizardView(false));
      goToDashboardPage();
    }
  };

  const saveStepProgress = (key) => {
    if (!key) {
      return false;
    }

    clearServerError();

    let step = key.toLowerCase();
    if (canAccessRelistingFlow && key === STEP.REVIEW) {
      step = `${step}${STEP_DELIMITER}${RELISTING_STEP_SUFFIX}`;
    }
    const params = {
      propertyId: listingId,
      step,
    };

    return dispatch(PostingActions.property.modularListingProgress(params))?.catch((errorRMWModel) => {
      const { message } = errorRMWModel;

      actions.updateServerError({
        reducerDispatch,
        serverErrorObj: WizardServerError({
          displayAt: DISPLAY_SERVER_ERROR_AT.PAGE,
          fieldName: serverErrorFieldName.saveAndExitError,
          message,
          displayMessage: message,
        }),
      });

      return false;
    });
  };

  const updateConfigObj = (responseConfigObj) => {
    if (!responseConfigObj) {
      return;
    }

    setConfigObj(responseConfigObj);
  };

  const trackContinueFrom = (currentStepKey) => {
    ModularListingCSEvents.ContinueFrom({ currentProperty, user, currentStepKey });
  };

  const trackSaveAndExitClick = (currentStepKey) => {
    ModularListingCSEvents.SaveAndExit({ currentProperty, user, currentStepKey });
  };

  const handleGoToStep = (key, { stepDirection = '' } = {}) => {
    window.scroll(0, 0);

    if (isPremiumPurchaseModalOpen) {
      displayPremiumPurchaseModal(false, true);
    }

    if (stepDirection === STEP_DIRECTION.NEXT) {
      ModularListingCSEvents.Next({ currentProperty, user, currentStepKey: key });
    }

    setCurrentStepKey(key);
    saveStepProgress(key);

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });
  };

  const handleSaveAndExit = async () => {
    if (isFeed) {
      exit();
    }
    displayPremiumPurchaseModal(false, isPremiumPurchaseModalOpen);
    if (currentStepKey === STEP.REVIEW && hasConfirmedSaveAndExit === false && !active) {
      actions.updateDisplaySaveAndExitModal({ reducerDispatch, displaySaveAndExitModal: true });
      return;
    }

    trackSaveAndExitClick(currentStepKey);
    const isSaveSuccess = await saveStepProgress(currentStepKey);

    if (!isSaveSuccess) {
      return;
    }

    actions.updateHasConfirmedSaveAndExit({ reducerDispatch, hasConfirmedSaveAndExit: false });

    exit();
  };

  /**
   * Set default step for different flows
   *
   * @returns {string} - default step key
   */
  const getDefaultStep = () => {
    const canAccessMLReactivationFlow = accessUtils.canAccessMLReactivationFlow({
      currentProperty,
    });
    if (canAccessRelistingFlow) {
      return DEFAULT_STEP_FOR_RELISTING;
    }

    if (canAccessMLReactivationFlow) {
      return DEFAULT_STEP_FOR_REACTIVATION;
    }

    return DEFAULT_STEP;
  };

  const handleExitWizard = () => {
    if (isWizardView) {
      handleSaveAndExit();
    }
  };

  const displayPremiumPurchaseModal = (isOpen, isExitingFlow = false) => {
    actions.updateIsPremiumPurchaseModalOpen({
      reducerDispatch,
      isPremiumPurchaseModalOpen: isOpen,
    });

    // update payment gateway context if user exits midfunnel upgrade modal
    if (!isOpen && isExitingFlow) {
      pgActions.updateDisplayPaymentGateway({
        reducerDispatch: pgReducerDispatch,
        displayPaymentGateway: false,
        isPremiumMidfunnelPayment: false,
      });
    }
  };

  const updateSelectedProductType = (selectedProductType) => {
    dispatch(
      PropertiesActions.updateCurrentProperty({
        selectedProductType,
      }),
    );
  };

  const handleSelectPremiumUpgrade = () => {
    updateSelectedProductType(PRODUCT_TYPE_NAME_TO_ID.PREMIUM);

    pgActions.updateDisplayPaymentGateway({
      reducerDispatch: pgReducerDispatch,
      displayPaymentGateway: true,
      isPremiumMidfunnelPayment: true,
    });

    displayPremiumPurchaseModal(false, false);
    ModularListingCSEvents.ClickUpgradeToPremiumFromModal({ currentProperty, user });
  };

  /**
   * check if currentStepKey is a valid step within the relisting flow
   */
  const isValidRelistingStep = ({ currentStepKey, canAccessPostingPathTourScheduling }) => {
    const reviewStep = configStepGroupReview.find((obj) => obj?.stepKey === STEP.REVIEW);
    const stepGroup = [...configStepGroupRelist, reviewStep];

    if (canAccessPostingPathTourScheduling) {
      const tourSchedulingBookingMethodRelistStep = configStepGroupTourBooking.find(
        (obj) => obj?.stepKey === STEP.TOUR_SCHEDULING_BOOKING_METHOD_RELIST,
      );
      stepGroup.push(tourSchedulingBookingMethodRelistStep);
    }

    let isValidStep = false;
    for (const obj of stepGroup) {
      if (obj?.stepKey === currentStepKey) {
        isValidStep = true;

        break;
      }
    }

    return isValidStep;
  };

  if (fetchListingError !== null) {
    switch (fetchListingError?.httpStatus) {
      case '400':
        return <ErrorPage type={ERROR_PAGE_TYPES.NOT_FOUND} />;
      case '403':
        return <ErrorPage type={ERROR_PAGE_TYPES.UNAUTHORIZED} />;
      default:
        return <ErrorPage type={ERROR_PAGE_TYPES.TECHNICAL_DIFFICULTIES} />;
    }
  }

  // check loading before PermissionCheck as this is dependent on an updated currentProperty
  return (
    <div
      data-testid="CreateListingPage"
      data-eventid={
        currentProperty?.active
          ? ReviewPageCSEvents.ActiveListingView.data_event_id
          : ReviewPageCSEvents.OffMarketListingView.data_event_id
      }
    >
      <HeadWrapper title={titleContent} />
      <AuthCheckWrapper>
        {!isLoading && !isToursLoading && isConfigStable ? (
          <TourSchedulingProvider
            currentStepKey={currentStepKey}
            canAccessPostingPathTourScheduling={canAccessPostingPathTourScheduling}
            selectedBookingMethod={selectedBookingMethodForTourScheduling}
            setSelectedBookingMethod={setSelectedBookingMethodForTourScheduling}
          >
            <PermissionCheck route={isInReactivationFlow ? LISTING : CREATE_LISTING_PAGE}>
              <div className="CreateListingPage fs-unmask">
                <WizardStepController
                  steps={CONFIG}
                  progressSteps={progressSteps}
                  currentStepKey={currentStepKey}
                  onGoToStep={handleGoToStep}
                  wizardTitle={wizardTitle}
                  onExitWizard={handleExitWizard}
                  exitCTALabel={EXIT_CTA_LABEL.SAVE_AND_EXIT}
                  labelAppearanceBreakpoint="xl"
                  showPageWizardDesktopViewBreakpoint="xxl"
                  isWizardComplete={isWizardComplete}
                  isBackDisabled={isBackDisabled}
                  isNextDisabled={isNextDisabled}
                />
              </div>
              {canAccessTopOfFunnelPremium && isPremiumPurchaseModalOpen && (
                <PremiumPurchaseModal
                  isOpen={isPremiumPurchaseModalOpen}
                  onClose={() => displayPremiumPurchaseModal(false, true)}
                  onSelectUpgrade={handleSelectPremiumUpgrade}
                  freemiumProducts={freemiumProducts}
                />
              )}
              {displayAddPropertyModal && (
                <AddPropertyModal
                  isOpen={displayAddPropertyModal}
                  onClose={() => setDisplayAddPropertyModal(false)}
                  behavior={AddPropertyConstants.BEHAVIOR_OPTIONS.EDIT}
                />
              )}
              <TransferFeedListingModal />
            </PermissionCheck>
          </TourSchedulingProvider>
        ) : (
          <SpinnerContainer size="lg" height="100vh" />
        )}
      </AuthCheckWrapper>
    </div>
  );
};

CreateListingPage.propTypes = propTypes;
CreateListingPage.defaultProps = defaultProps;

export default CreateListingPage;

export { DEFAULT_STEP };
